/**
 * MailPage — proposta A (2026-07-02): 3 colonne stile Messaggi.
 *
 * ┌ lista outbox ─┬─ thread ────────────┬─ contesto ──────┐
 * │ ricerca       │ bolle inviate/      │ dati ordine     │
 * │ chip stato    │ ricevute + Rispondi │ allegati        │
 * │ fornitore/    │                     │                 │
 * │ periodo, righe│                     │                 │
 * └───────────────┴─────────────────────┴─────────────────┘
 *
 * Selezione = deep-link `/mail/:ref` (useParams/useNavigate), invariata.
 * Colonne ridimensionabili con divisori persistiti (useMailPanelWidths,
 * stessa meccanica della pagina Messaggi). Il contesto ordine sta SEMPRE
 * in vista nella colonna destra (prima era impilato sotto il thread).
 */
import { useEffect, useState } from "react";
import {
  contorno,
  FORMA_BADGE,
  pieno,
  type StatoTone,
} from "@/features/ordini/components/statoToni";
import { ChipFiltro } from "@/shared/ui/ChipFiltro";
import { useNavigate, useParams } from "react-router-dom";
import { prettifyApiError } from "@/shared/api";
import { hasActiveTextSelection } from "@/shared/textSelection";
import { PanelResizer } from "@/shared/ui/PanelResizer";
import { useIsMobile } from "@/shared/useMediaQuery";
import { fetchOutbox, outboxAttachmentUrl } from "./api";
import type { OutboxRead, OutboxRow } from "./api";
import { useMailPanelWidths } from "./mailPanelWidths";
import { MailThreadPanel } from "./MailThreadPage";
import { OrderInfoPanel } from "./OrderInfoPanel";

// ---------------------------------------------------------------------------
// Badge stato — stessi token/classi di MailStatusBadge (ordini/components).
// ---------------------------------------------------------------------------

// Redesign 27/10: forma e palette dei badge della lista ordini. «In attesa»
// e' a contorno (aspetta la conferma), confermata e fallita sono piene.
const STATUS_PRESETS: Record<string, { label: string; tono: StatoTone; pieno: boolean }> = {
  sent: { label: "In attesa", tono: "in-corso", pieno: false },
  sending: { label: "In invio", tono: "in-corso", pieno: false },
  imap_confirmed: { label: "Confermata", tono: "ordinato", pieno: true },
  failed: { label: "Fallita", tono: "scaduta", pieno: true },
};

function OutboxStatusBadge({ status }: { status: string }) {
  const preset = STATUS_PRESETS[status] ?? {
    label: status,
    tono: "annullato" as const,
    pieno: false,
  };
  return (
    <span className={FORMA_BADGE} style={preset.pieno ? pieno(preset.tono) : contorno(preset.tono)}>
      {preset.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Filtri colonna sinistra
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
  { value: "", label: "Tutte" },
  { value: "sent", label: "In attesa" },
  { value: "imap_confirmed", label: "Confermate" },
  { value: "failed", label: "Fallite" },
];

const PERIOD_OPTIONS = [
  { value: "", label: "Tutte" },
  { value: "7", label: "Ultimi 7 giorni" },
  { value: "30", label: "Ultimi 30 giorni" },
];

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** Il tono dei chip stato: quello del badge che filtrano. */
const TONO_FILTRO: Record<string, StatoTone> = {
  "": "annullato",
  sent: "in-corso",
  imap_confirmed: "ordinato",
  failed: "scaduta",
};

export function MailPage() {
  const { ref } = useParams<{ ref?: string }>();
  const navigate = useNavigate();
  const panels = useMailPanelWidths();
  // Telefono: un pannello alla volta, deciso dall'URL come sul computer —
  // `/mail` = la lista, `/mail/:ref` = il thread con sotto il contesto
  // dell'ordine. Il «indietro» di Android torna alla lista da solo.
  const isMobile = useIsMobile();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [supplier, setSupplier] = useState("");
  const [status, setStatus] = useState("");
  const [days, setDays] = useState("");

  const PAGE_SIZE = 100;

  // counts/total dal primo fetch della vista corrente (filtri); rows accumula
  // per "Carica altre" mantenendo lo stesso denominatore.
  const [meta, setMeta] = useState<{ counts: OutboxRead["counts"]; total: number } | null>(null);
  const [rows, setRows] = useState<OutboxRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Errore della SOLA paginazione, tenuto separato da `error`: righe e footer
  // sono gatati su `!error`, quindi condividerlo faceva sparire tutta la lista
  // già caricata al primo «Carica altre» fallito.
  const [moreError, setMoreError] = useState<string | null>(null);

  // Debounce ricerca (300ms): evita un fetch per ogni keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  // Cambio filtri/ricerca → reset paginazione e ricarica dalla prima pagina.
  useEffect(() => {
    const ctrl = new AbortController();
    setIsLoading(true);
    setMoreError(null);
    fetchOutbox({
      supplier: supplier || undefined,
      status: status || undefined,
      q: debouncedSearch || undefined,
      days: days ? Number(days) : undefined,
      limit: PAGE_SIZE,
      offset: 0,
    })
      .then((res) => {
        if (ctrl.signal.aborted) return;
        setRows(res.rows);
        setMeta({ counts: res.counts, total: res.total });
        setError(null);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        setError(prettifyApiError(err));
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setIsLoading(false);
      });
    return () => ctrl.abort();
  }, [supplier, status, debouncedSearch, days]);

  function loadMore(): void {
    const nextOffset = rows.length;
    // Snapshot dei filtri al click: se cambiano mentre la fetch e' in volo,
    // l'effetto principale resetta rows alla pagina 1 dei nuovi filtri; senza
    // questa guardia la promise stale appenderebbe in coda le righe dei VECCHI
    // filtri → lista mista che non rispetta il filtro attivo.
    const snap = {
      supplier: supplier || undefined,
      status: status || undefined,
      q: debouncedSearch || undefined,
      days: days ? Number(days) : undefined,
    };
    setIsLoadingMore(true);
    setMoreError(null);
    fetchOutbox({
      ...snap,
      limit: PAGE_SIZE,
      offset: nextOffset,
    })
      .then((res) => {
        // Filtri cambiati durante la fetch: scarta il risultato stale.
        if (
          snap.supplier !== (supplier || undefined) ||
          snap.status !== (status || undefined) ||
          snap.q !== (debouncedSearch || undefined) ||
          snap.days !== (days ? Number(days) : undefined)
        ) {
          return;
        }
        setRows((prev) => [...prev, ...res.rows]);
      })
      .catch((err: unknown) => setMoreError(prettifyApiError(err)))
      .finally(() => setIsLoadingMore(false));
  }

  const total = meta?.total ?? 0;
  const hasMore = rows.length < total;
  const selectedRow: OutboxRow | undefined = rows.find((r) => r.order_ref === ref);
  const chipCounts: Record<string, number | undefined> = {
    sent: meta?.counts.pending,
    failed: meta?.counts.failed,
  };

  return (
    <main
      className={
        isMobile
          ? "flex h-full min-h-0 flex-1 flex-col overflow-y-auto bg-paper"
          : "grid h-full min-h-0 flex-1 overflow-hidden bg-paper"
      }
      style={
        isMobile ? undefined : { gridTemplateColumns: panels.gridTemplateColumns, minHeight: 0 }
      }
    >
      {/* Colonna sinistra: outbox filtrabile */}
      <aside
        className={`flex min-h-0 flex-col overflow-hidden border-r border-line bg-card max-md:flex-1 max-md:border-r-0 ${isMobile && ref ? "hidden" : ""}`}
      >
        <div className="border-b border-line p-3.5 max-md:p-3">
          <h1 className="mb-2 font-display text-[19px] font-bold max-md:sr-only">Mail</h1>

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per ordine, oggetto…"
            aria-label="Cerca"
            className="h-[34px] w-full rounded-full border border-line-2 bg-card px-3.5 text-[13px] text-ink placeholder:text-ink-3 outline-none transition-[border-color,box-shadow] focus:border-ink-3 focus:shadow-[0_0_0_3px_rgba(31,27,22,0.06)] max-md:h-12 max-md:rounded-full max-md:border-line-2 max-md:px-4 max-md:text-[16px] max-md:placeholder:text-ink-3"
          />

          {/* Chip stato: sostituiscono la select (proposta A) — un click,
              conteggi in vista (in attesa/fallite dai counts backend). */}
          <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-label="Filtro stato">
            {STATUS_OPTIONS.map((opt) => (
              <ChipFiltro
                key={opt.value || "all"}
                tono={TONO_FILTRO[opt.value] ?? "annullato"}
                attivo={status === opt.value}
                onClick={() => setStatus(opt.value)}
                conteggio={chipCounts[opt.value] || undefined}
              >
                {opt.label}
              </ChipFiltro>
            ))}
          </div>

          <div className="mt-2 grid grid-cols-2 gap-2">
            <select
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              aria-label="Fornitore"
              className="h-[34px] w-full cursor-pointer rounded-full border border-line-2 bg-card px-3 text-[12.5px] font-medium text-ink outline-none transition-colors hover:border-ink-4 focus:border-ink-3 max-md:h-11 max-md:text-[15px]"
            >
              <option value="">Fornitore</option>
              {Object.entries(meta?.counts.by_supplier ?? {}).map(([name, count]) => (
                <option key={name} value={name}>
                  {name} ({count})
                </option>
              ))}
            </select>

            <select
              value={days}
              onChange={(e) => setDays(e.target.value)}
              aria-label="Periodo"
              className="h-[34px] w-full cursor-pointer rounded-full border border-line-2 bg-card px-3 text-[12.5px] font-medium text-ink outline-none transition-colors hover:border-ink-4 focus:border-ink-3 max-md:h-11 max-md:text-[15px]"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-1 flex-col gap-1.5 overflow-y-auto bg-paper p-2.5 scroll-paper max-md:bg-transparent max-md:p-0 max-md:pt-2">
          {isLoading && <p className="p-4 text-[12.5px] text-ink-2">Caricamento…</p>}
          {!isLoading && error && (
            <p role="alert" className="p-4 text-[12px] text-red">
              {error}
            </p>
          )}
          {!isLoading && !error && rows.length === 0 && (
            <p className="p-4 text-[12.5px] text-ink-2">Nessuna mail trovata.</p>
          )}
          {!isLoading &&
            !error &&
            rows.map((row) => {
              return (
                <button
                  key={row.id}
                  type="button"
                  data-status={row.status}
                  onClick={() => {
                    // Selezione testo in corso (copia ref/oggetto): no navigate.
                    if (hasActiveTextSelection()) return;
                    void navigate(`/mail/${encodeURIComponent(row.order_ref)}`);
                  }}
                  aria-current={row.order_ref === ref ? "true" : undefined}
                  className={`w-full rounded-xl border bg-card px-3.5 py-2.5 text-left shadow-[0_1px_2px_rgba(31,27,22,0.04)] transition-colors max-md:py-3.5 max-md:[&_p]:text-[14px] max-md:[&_p]:text-ink max-md:[&_time]:text-[13px] max-md:[&_time]:text-ink ${
                    row.order_ref === ref
                      ? "border-ink shadow-[0_0_0_1px_rgb(var(--om-ink))]"
                      : "border-line hover:border-ink-4"
                  }`}
                >
                  <div className="mb-0.5 flex items-center justify-between gap-2">
                    <span className="truncate font-display text-[14px] font-semibold text-ink max-md:font-display max-md:text-[16px] max-md:font-semibold">
                      {row.order_ref}
                    </span>
                    <OutboxStatusBadge status={row.status} />
                  </div>
                  <p className="truncate text-[12.5px] text-ink">{row.subject}</p>
                  <div className="flex items-center gap-1.5">
                    <time className="text-[11.5px] tabular-nums text-ink-2">
                      {formatDate(row.sent_at)}
                    </time>
                    {row.attachment_names.length > 0 && (
                      <span className="text-[11.5px] text-ink-2">
                        · {row.attachment_names.length} alleg.
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          {!isLoading && !error && rows.length > 0 && (
            <div className="flex flex-col items-center gap-2 p-4">
              <p className="text-[12px] text-ink-2">
                {rows.length} di {total}
              </p>
              {moreError && (
                <p role="alert" className="text-center text-[11.5px] text-red">
                  {moreError}
                </p>
              )}
              {hasMore && (
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="text-[12px] text-accent hover:underline disabled:opacity-50"
                >
                  {isLoadingMore ? "Caricamento…" : "Carica altre"}
                </button>
              )}
            </div>
          )}
        </div>
      </aside>

      {!isMobile && (
        <PanelResizer
          label="Ridimensiona lista mail"
          value={panels.widths.list}
          direction={1}
          onChange={(px) => panels.setWidth("list", px)}
          onReset={() => panels.resetWidth("list")}
        />
      )}

      {/* Colonna centrale: thread dell'ordine selezionato */}
      <section
        className={`flex min-h-0 flex-col overflow-hidden max-md:shrink-0 max-md:overflow-visible ${isMobile && !ref ? "hidden" : ""}`}
      >
        {!ref && (
          <div className="flex h-full items-center justify-center text-[13px] text-ink-2">
            Seleziona una mail.
          </div>
        )}
        {ref && (
          <>
            <header className="border-b border-line bg-card px-5 py-3 max-md:sticky max-md:top-0 max-md:z-10 max-md:flex max-md:items-center max-md:gap-1 max-md:bg-paper max-md:px-1 max-md:py-1.5">
              {isMobile && (
                <button
                  type="button"
                  onClick={() => void navigate("/mail")}
                  aria-label="Torna alla lista mail"
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-ink active:bg-paper-2"
                >
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
              )}
              <div className="min-w-0">
                <h2 className="font-display text-[16px] font-bold text-ink max-md:truncate max-md:font-display max-md:text-[17px] max-md:font-semibold">
                  {isMobile ? ref : `Thread mail — ${ref}`}
                </h2>
                <p className="text-[12.5px] text-ink-2 max-md:text-[13px] max-md:text-ink">
                  {isMobile
                    ? "Inviate a sinistra, ricevute a destra"
                    : "Inviate a sinistra, ricevute dal fornitore a destra"}
                </p>
              </div>
            </header>
            <div className="scroll-paper flex-1 overflow-y-auto px-5 py-4 max-md:flex-none max-md:overflow-visible max-md:px-3">
              <MailThreadPanel orderRef={ref} />
            </div>
          </>
        )}
      </section>

      {!isMobile && (
        <PanelResizer
          label="Ridimensiona contesto ordine"
          value={panels.widths.context}
          direction={-1}
          onChange={(px) => panels.setWidth("context", px)}
          onReset={() => panels.resetWidth("context")}
        />
      )}

      {/* Colonna destra: contesto ordine + allegati, sempre in vista */}
      <aside
        className={`min-h-0 space-y-3 overflow-y-auto border-l border-line bg-card p-3.5 max-md:shrink-0 max-md:overflow-visible max-md:border-l-0 max-md:border-t max-md:p-3 ${isMobile && !ref ? "hidden" : ""}`}
      >
        {!ref && (
          <p className="pt-4 text-center text-[12.5px] text-ink-2">
            Il contesto dell'ordine appare qui.
          </p>
        )}
        {ref && (
          <>
            <OrderInfoPanel orderRef={ref} />
            {selectedRow && selectedRow.attachment_names.length > 0 && (
              <div className="rounded-xl border border-line p-4">
                <h2 className="mb-2 font-display text-[14px] font-bold text-ink">Allegati</h2>
                <ul className="space-y-1">
                  {selectedRow.attachment_names.map((name, i) => (
                    <li key={name}>
                      <a
                        href={outboxAttachmentUrl(selectedRow.id, i)}
                        download
                        className="text-[11.5px] text-accent underline decoration-dotted underline-offset-2 hover:decoration-solid break-all"
                      >
                        {name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </aside>
    </main>
  );
}
